import React, { useEffect, useState } from 'react';
import { Dialog } from '../../../components/CommonComponents/Dialog/Dialog';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import WarningImg from "../../../assets/images/svgs/yellowwarming.svg";
import style from "../../../pages/forgotPassword/forgotPassword.module.css";
import { BASE_URL } from '../../../constants/Constants';
import { _axios } from '../../../utils/axios';
import { ErrorToast } from "../../toastMessage";

type OverrideDialogProps = {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    overrideReason: string;
    setOverrideReason: (reason: string) => void;
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    overrideType?: "application" | "payment";
    /** The application's status, so a payment override names the levels it skips. */
    overrideStatus?: string;
};

const OverrideDialog: React.FC<OverrideDialogProps> = ({
    show,
    onHide,
    onConfirm,
    overrideReason,
    setOverrideReason,
    textAreaRef,
    overrideType,
    overrideStatus
}) => {
    const [existingLogo, setExistingLogo] = useState<string>('');
    const [logoUrl, setLogoUrl] = useState<string>('');

    /**
     * BEFORE - a payment override always named Level 2 only, which is wrong
     * at Level 1's payment review (it skips Level 1 and Level 2):
     *
     * const message = overrideType === "payment"
     *     ? "Do you want to override the Level 2 payment approvals? Once the approvals are overridden, you cannot undo it."
     *     : ...
     */
    const paymentLevels = overrideStatus === "Pending Level 1 Final Review" ? "Level 1 & Level 2" : "Level 2";
    const message = overrideType === "payment"
        ? `Do you want to override the ${paymentLevels} payment approvals? Once the approvals are overridden, you cannot undo it.`
        : "Do you want to override the Level 1 & Level 2 approvals? Once the approvals are overridden, you cannot undo it.";

    const handleConfirm = () => {
        if (!textAreaRef.current?.value.trim()) {
            ErrorToast("Override reason is required.");
            return;
        }
        onConfirm();
    };

    useEffect(() => {
        const fetchImages = async () => {
            try {
                // Fetch logo
                const logoResponse = await _axios.get(`${BASE_URL}/api/logo`);
                if (logoResponse.data.success === 1) {
                    setLogoUrl(logoResponse.data.url);
                    if (logoResponse.data.filename) {
                        setExistingLogo(logoResponse.data.filename);
                    }
                }
            } catch (error) {
                console.error('Error fetching images:', error);
            }
        };

        fetchImages();
    }, []);

    return (
        <Dialog open={show} onClose={onHide}>
            <div className="w-full max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl py-4 sm:py-6 md:py-8 lg:py-10 xl:py-6 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 cursor-pointer">
                <div className="p-2  flex justify-center items-center">
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt="Logo"
                            className={style.companyIcon}
                        />
                    )}
                </div>

                <div className="w-full text-center mb-4">
                    <p className="text-base text-gray-600">
                        {message}
                    </p>
                </div>

                <Form.Group controlId="overrideReason" className="w-full mb-4">
                    <Form.Label className="text-sm font-semibold">Override Reason</Form.Label>
                    <textarea
                        ref={textAreaRef}
                        className="w-full p-2 text-sm border rounded-lg"
                        rows={3}
                        style={{ height: '120px', minHeight: '120px' }}
                        placeholder="Enter Override Reason"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                    ></textarea>
                </Form.Group>

                <div className="flex justify-center space-x-4 w-full">


                    <button
                        className="bg-primary px-6 py-2 font-bold text-white rounded-full"
                        onClick={handleConfirm}
                    >
                        Override
                    </button>
                    <button
                        className="bg-gray-500 px-6 py-2 font-bold text-white rounded-full"
                        onClick={onHide}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default OverrideDialog;
